import { modals } from '@mantine/modals';
import { api } from '~/trpc-extra/api';
import { useProjectId } from './context/useProjectId';
import { useDataTable } from '~/components/tables/common/useDataTable';
import DataTableSearchBox from '~/components/tables/common/DataTableSearchBox';
import { DataTable } from '~/components/tables/common/DataTable';
import { DataTableResultCount } from '~/components/tables/common/DataTableResultCount';
import { testSuiteColumns } from '~/components/tables/testSuite/testSuiteColumns';
import { useMemo } from 'react';
// The hook returns a function that triggers the test suite dialog
export function useTestSuiteDialog({
  onSuiteClick,
}: {
  onSuiteClick: ((testSuiteId: string) => void) | ((testSuiteId: string) => Promise<void>);
}) {
  const openTestSuiteDialog = () => {
    modals.open({
      size: 'xl',
      title: (
        <div className="flex w-full items-center justify-between px-2">
          <div className="text-sm font-medium">Add to a test suite</div>
        </div>
      ),
      zIndex: 310,
      centered: true,
      children: (
        <TestSuiteDialog
          onSuiteClick={async (testSuiteId) => {
            await onSuiteClick(testSuiteId);
            modals.closeAll();
          }}
        />
      ),
    });
  };

  return openTestSuiteDialog;
}

function TestSuiteDialog({ onSuiteClick }: { onSuiteClick: (testSuiteId: string) => void }) {
  const projectId = useProjectId();
  const { data: testSuites = [] } = api.testSuite.listWithLatestRun.useQuery({
    projectId: projectId || '',
  });

  const noActionOrRunTable = useMemo(
    () => testSuiteColumns.filter((col) => col.id !== 'actions' && col.id !== 'runButton'),
    [],
  );
  const table = useDataTable({ columns: noActionOrRunTable, data: testSuites, enableNavigation: false });

  return (
    <div className="flex h-full flex-col gap-2 px-2 py-4">
      <div className="flex items-center gap-2">
        <DataTableSearchBox table={table} />
        <DataTableResultCount
          table={table}
          suffix={`suite${testSuites.length === 1 ? '' : 's'}`}
        />
      </div>
      <DataTable
        table={table}
        onRowClick={(row) => onSuiteClick(row.original.id)}
      />
    </div>
  );
}
